import type { DataOption } from '@/common.types'
import type { SRT_TableInstance } from '@/shared/components/master-table'
import type { FieldSchemaModel } from '@/shared/generated'

import React from 'react'
import { Link } from '@tanstack/react-router'
import { differenceInDays, formatDistanceToNow } from 'date-fns'
import { find } from 'lodash/fp'
import { CheckIcon, XIcon } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { DeltaValue } from '@/shared/components/ui/delta-value'
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/shared/components/ui/hover-card'
import { Tag } from '@/shared/components/ui/tag'
import { getLangCode, useLocale } from '@/shared/i18n'
import { intlFormat } from '@/shared/tools/dates'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { RelativeTimeCard } from '../../ui/relative-time-card'
import { ColumnDataType } from '../types'
import CheckboxFieldEditor from './Checkbox/TableCellEditor'
import NumberField from './Number/TableCell'
import NumberFieldEditor from './Number/TableCellEditor'
import PlainTextField from './PlainText/TableCell'
import PlainTextFieldEditor from './PlainText/TableCellEditor'

/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */

export type Choice = {
	variant?: string
	color?: string
	icon?: React.ReactNode
} & DataOption

export type CellProp<TValue extends any = any, TRow extends any = any> = {
	/**
	 * Current row value
	 */
	value: TValue
	/**
	 * Row original initial value
	 */
	originalValue: TValue

	/**
	 * Columns schema model
	 */
	schema?: {
		editable?: boolean
		type?: ColumnDataType
	}

	/**
	 * Row data object
	 */
	row: TRow

	/**
	 * Choices for choice fields
	 */
	choices?: DataOption[]
	isDirty?: boolean
	isDisabled?: boolean
	onChange?(value: TValue): void
	table?: SRT_TableInstance<any>
}

export type CellPropControlled<
	TValue extends any = any,
	TRow extends any = any,
> = {
	schema: Partial<FieldSchemaModel>
} & CellProp<TValue, TRow>
export function DatetimeField(props: Partial<CellProp>) {
	return props.value ? intlFormat(new Date(props.value + 'Z')) : '-'
}
export function DatetimeDistanceField(props: Partial<CellProp>) {
	const currentLocale = useLocale()
	const value = props.value ? new Date(props.value) : null
	if (value) {
		const diff = differenceInDays(value, new Date())
		if (diff > 5) {
			return (
				<RelativeTimeCard date={value} locale={getLangCode(currentLocale)}>
					<span>{intlFormat(value)}</span>
				</RelativeTimeCard>
			)
		}

		return (
			<RelativeTimeCard date={value} locale={getLangCode(currentLocale)}>
				<span>{formatDistanceToNow(value)}</span>
			</RelativeTimeCard>
		)
	}

	return '-'
}
export function SizeField(props: CellProp) {
	return <div className='cell-data-number'>{props.value?.length}</div>
}
export function LinkField(props: CellProp) {
	return <a href={props.value}>{props.value}</a>
}
export function InternalLinkField(props: CellProp) {
	return <Link to={props.value}>{props.value}</Link>
}
export function PercentageField(props: CellProp) {
	return (
		<div className='cell-data-number'>
			{formatNumber(props.value, NumberFormats.Percent)}
		</div>
	)
}
export function BadgeField(props: CellProp) {
	const getChoice = (value: string | boolean | number | null) =>
		find({ value }, props.choices) || null
	const items = (Array.isArray(props.value) ? props.value : [props.value])
		?.map(getChoice)
		?.filter(Boolean) as Choice[]

	return (
		<div className='flex items-center space-x-1'>
			{items.length > 0
				? items?.map(({ value, label, variant, icon }) => (
						// FIXME: Badge variant type

						<Badge className='shrink-0' variant={variant as any} key={value}>
							{icon}
							{label}
						</Badge>
					))
				: '-'}
		</div>
	)
}
export function ChipField(props: CellProp) {
	const getChoice = (value: string | boolean | number | null) =>
		find({ value }, props.choices) || null
	const items = (Array.isArray(props.value) ? props.value : [props.value])
		?.map(getChoice)
		?.filter(Boolean) as Choice[]

	return (
		<div className='flex items-center space-x-1'>
			{items.length > 0
				? items?.map(({ value, label, variant, icon }) => (
						// FIXME: Badge variant type

						<Tag className='shrink-0' variant={variant as any} key={value}>
							{icon}
							{label}
						</Tag>
					))
				: '-'}
		</div>
	)
}
export function ChoiceField(props: CellProp) {
	const items = Array.isArray(props.value) ? props.value : [props.value]
	const getChoice = (value: string | boolean | number | null) =>
		(find({ value }, props.choices) as Choice) || { value, label: value }

	return (
		<div className='flex items-center space-x-1'>
			{items?.length > 0
				? items
						?.map(getChoice)
						?.map(({ value, label }) => <span key={value}>{label}</span>)
				: '-'}
		</div>
	)
}
export function IndicatorField(props: CellProp) {
	const items = Array.isArray(props.value) ? props.value : [props.value]
	const getChoice = (value: string | boolean | number | null) =>
		(find({ value }, props.choices) as Choice) || { value, label: value }

	return (
		<div className='flex items-center space-x-1'>
			{items?.length > 0
				? items?.map(getChoice)?.map(({ value, label, color }) => (
						<span key={value} className='inline-flex items-center'>
							<span
								className='mr-2 size-1.5 shrink-0 rounded-full'
								style={{ backgroundColor: color }}
							/>
							{label}
						</span>
					))
				: '-'}
		</div>
	)
}
export function DifferenceField(props: CellProp) {
	return (
		<DeltaValue value={props.value}>
			<NumberField {...props} />
		</DeltaValue>
	)
}
export function FlagField(props: Partial<CellProp>) {
	if (props.value === undefined || props.value === null) {
		return <>-</>
	}

	return props.value ? (
		<CheckIcon className='size-4 text-success' />
	) : (
		<XIcon className='size-4 text-destructive' />
	)
}
export function ImageField({ value }: { value: string | null | undefined }) {
	if (!value) {
		return (
			<div className='flex cursor-not-allowed items-center'>
				<div className='relative flex h-8 w-14 items-center justify-center overflow-hidden rounded-md border border-solid border-border bg-secondary'>
					<img src='/no-image-small.svg' />
				</div>
			</div>
		)
	}
	const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
		e.currentTarget.classList.add('cursor-pointer')
	}
	const onError = (e: React.SyntheticEvent<HTMLImageElement>) => {
		e.currentTarget.classList.add('pointer-events-none')
	}

	return (
		<div className='flex items-center'>
			<HoverCard>
				<HoverCardTrigger>
					<div className='relative flex h-8 w-14 items-center justify-center overflow-hidden rounded-lg border hover:border-primary'>
						<img
							loading='lazy'
							className='image-type'
							src={value}
							onLoad={onLoad}
							onError={onError}
						/>
					</div>
				</HoverCardTrigger>
				<HoverCardContent
					align='center'
					side='right'
					className='overflow-hidden p-0'
				>
					<img
						className='image-type'
						src={value}
						onLoad={onLoad}
						onError={onError}
					/>
				</HoverCardContent>
			</HoverCard>
		</div>
	)
}

/**
 * Get component for rendering cell value by schema type
 * @param cellType ColumnSchemaModel.type
 * @returns  React.ElementType<CellProp>
 */
export function getFieldBySchemaType(
	cellType: string
): React.ComponentType<CellProp> {
	switch (cellType) {
		case ColumnDataType.Integer:
		case ColumnDataType.Float:
		case 'number':
			return NumberField
		case 'title':
		case ColumnDataType.String:
			return PlainTextField
		case 'diff':
			return DifferenceField
		case 'badge':
			return BadgeField
		case 'image':
			return ImageField
		case ColumnDataType.Datetime:
			return DatetimeField
		case 'size':
			return SizeField
		case ColumnDataType.Boolean:
			return FlagField
		default:
			return PlainTextField
	}
}

/**
 * Get component for editing cell value by schema type
 * @param cellType  ColumnSchemaModel.type
 * @returns - React.ElementType<CellProp>
 */
export function getFieldEditorBySchemaType(
	cellType: string
): React.ComponentType<CellPropControlled> {
	switch (cellType) {
		case ColumnDataType.Integer:
		case ColumnDataType.Float:
		case 'number':
			return NumberFieldEditor
		case ColumnDataType.Boolean:
			return CheckboxFieldEditor
		default:
			return PlainTextFieldEditor
	}
}
